// Shared vocabulary for the Search screen: the four modes, the scope bar, AI entitlement states and small helpers
// used by search.js and its panels.

export const SEARCH_MODES = {
  INDEX: "index",
  TEXT: "text",
  SEMANTIC: "semantic",
  ASSISTANT: "assistant",
};

export const MODE_LABELS = {
  [SEARCH_MODES.INDEX]: "Index Search",
  [SEARCH_MODES.TEXT]: "Text Search",
  [SEARCH_MODES.SEMANTIC]: "Semantic Search",
  [SEARCH_MODES.ASSISTANT]: "AI Assistant",
};

export const AI_MODES = [SEARCH_MODES.SEMANTIC, SEARCH_MODES.ASSISTANT];

// The entitlement feature each AI mode needs (GET api/Entitlements/me -> features).
export const MODE_FEATURE = {
  [SEARCH_MODES.SEMANTIC]: "semanticSearch",
  [SEARCH_MODES.ASSISTANT]: "generativeAi",
};

// Sentinel value of the template picker meaning "every index template in scope".
export const ALL_TEMPLATES = "";

// Sentinel value of the repository picker meaning "every repository the user can see".
export const ALL_REPOSITORIES = "";

export const TEXT_MATCH_MODES = [
  { value: "all", label: "All of the words" },
  { value: "phrase", label: "Exact phrase" },
  { value: "any", label: "Any of the words" },
];

// Lock reasons returned by the API (AiLockReasons) and the copy shown for each.
export const LOCK_REASONS = {
  NO_TENANT: "NO_TENANT",
  TENANT_NOT_SUBSCRIBED: "TENANT_NOT_SUBSCRIBED",
  TENANT_SUSPENDED: "TENANT_SUSPENDED",
  TENANT_EXPIRED: "TENANT_EXPIRED",
  TENANT_NOT_STARTED: "TENANT_NOT_STARTED",
  FEATURE_NOT_INCLUDED: "FEATURE_NOT_INCLUDED",
  NO_USER_SEAT: "NO_USER_SEAT",
  QUOTA_EXCEEDED: "QUOTA_EXCEEDED",
  SERVICE_DISABLED: "SERVICE_DISABLED",
  UNAVAILABLE: "UNAVAILABLE",
};

export function lockCopy(reason, context = {}) {
  const client = context.clientName || "your organization";
  switch (reason) {
    case LOCK_REASONS.NO_USER_SEAT:
      return {
        title: "Your account does not have an AI seat yet",
        body: `DocumentSync AI is active for ${client}, but your user account has not been assigned a seat. Ask your DocumentSync administrator to assign one under Manage Users.`,
        action: "Request an AI seat",
      };
    case LOCK_REASONS.TENANT_SUSPENDED:
      return {
        title: "DocumentSync AI is suspended",
        body: `${client}'s DocumentSync AI subscription is currently suspended. Contact your Foveonics account manager to restore it.`,
        action: "Contact Foveonics",
      };
    case LOCK_REASONS.TENANT_EXPIRED:
      return {
        title: "DocumentSync AI subscription has ended",
        body: `${client}'s DocumentSync AI subscription has ended. Contact your Foveonics account manager to renew it.`,
        action: "Contact Foveonics",
      };
    case LOCK_REASONS.TENANT_NOT_STARTED:
      return {
        title: "DocumentSync AI starts soon",
        body: `${client}'s DocumentSync AI subscription has not started yet. The AI tabs unlock automatically on the start date.`,
        action: "Contact Foveonics",
      };
    case LOCK_REASONS.FEATURE_NOT_INCLUDED:
      return {
        title: "This feature is not in your subscription",
        body: `${client}'s DocumentSync AI subscription does not include this feature. Contact your Foveonics account manager to add it.`,
        action: "Ask about adding it",
      };
    case LOCK_REASONS.QUOTA_EXCEEDED:
      return {
        title: "This month's AI searches are used up",
        body: `${client} has used all of its AI searches for this month. They reset on the first of next month, or your Foveonics account manager can raise the limit.`,
        action: "Contact Foveonics",
      };
    case LOCK_REASONS.SERVICE_DISABLED:
      return {
        title: "AI features are temporarily unavailable",
        body: "DocumentSync AI is not available in this environment right now. Index Search and Text Search work as usual.",
        action: null,
      };
    case LOCK_REASONS.UNAVAILABLE:
      return {
        title: "AI features could not be checked",
        body: "DocumentSync could not confirm your AI subscription. Index Search and Text Search work as usual; try again in a moment.",
        action: null,
      };
    case LOCK_REASONS.NO_TENANT:
    case LOCK_REASONS.TENANT_NOT_SUBSCRIBED:
    default:
      return {
        title: "Part of DocumentSync AI",
        body: `DocumentSync AI is not included in ${client}'s current subscription. Once it is added, your DocumentSync administrator assigns AI seats to the users who need them.`,
        action: "Ask about adding DocumentSync AI",
      };
  }
}

// The entitlement used when GET api/Entitlements/me fails: AI tabs shown locked, nothing else changes.
export function unavailableEntitlement(hasTextSearch) {
  return {
    clientId: null,
    clientName: null,
    serviceAvailable: false,
    tenant: { subscribed: false, active: false },
    user: { hasSeat: false },
    features: {
      indexSearch: true,
      textSearch: Boolean(hasTextSearch),
      semanticSearch: false,
      generativeAi: false,
    },
    usage: {},
    lockReason: LOCK_REASONS.UNAVAILABLE,
    showLockedTabs: true,
    unavailable: true,
  };
}

// How an AI tab should appear for the current entitlement: "live", "locked" or "hidden".
export function aiTabState(mode, entitlement) {
  if (!entitlement) return "locked";
  const features = entitlement.features || {};
  const feature = MODE_FEATURE[mode];
  if (feature && features[feature]) return "live";
  const anyAi = Boolean(features.semanticSearch || features.generativeAi);
  if (anyAi) return "locked"; // the other AI feature is live: show this one locked with its own reason
  return entitlement.showLockedTabs === false ? "hidden" : "locked";
}

// How an AI tab appears for this user. With the server's seat gate on (entitlement.enforced) the entitlement decides.
// With the gate off (the default until every tenant's subscription and seats are set up) today's rule stands: only the
// logins on the AI allow-list see the AI tabs, and nobody else is shown them, live or locked.
export function aiAccessState(mode, entitlement, allowListed) {
  if (entitlement && entitlement.enforced === true) return aiTabState(mode, entitlement);
  if (!allowListed) return "hidden";
  return aiTabState(mode, entitlement);
}

// The reason an AI tab is locked, with the per-feature case when the tenant is otherwise fine.
export function aiTabLockReason(mode, entitlement) {
  if (!entitlement) return LOCK_REASONS.UNAVAILABLE;
  if (entitlement.lockReason) return entitlement.lockReason;
  const features = entitlement.features || {};
  const feature = MODE_FEATURE[mode];
  if (feature && !features[feature]) return LOCK_REASONS.FEATURE_NOT_INCLUDED;
  return null;
}

// Display name for a template table name, matching ChatSearch.formatTemplateName when no dictionary entry exists.
export function templateDisplayName(tableName, templates = []) {
  if (!tableName) return "";
  const match = (templates || []).find(
    (template) =>
      template &&
      typeof template.tableName === "string" &&
      template.tableName.toLowerCase() === String(tableName).toLowerCase(),
  );
  if (match && match.displayName) return String(match.displayName).trim();
  let s = String(tableName);
  s = s.replace(/^inx/i, "");
  s = s.replace(/^[_\s]+/, "");
  s = s.replace(/[_-]?\d+$/i, "");
  s = s.replace(/[_\s]+$/, "");
  s = s.replace(/_/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  return s.length > 0 ? s : String(tableName);
}

const ENTITY_MAP = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&#x27;": "'",
  "&apos;": "'",
};

export function decodeEntities(value) {
  return String(value || "").replace(
    /&(amp|lt|gt|quot|#39|#x27|apos);/g,
    (match) => ENTITY_MAP[match] || match,
  );
}

export function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Turns an OpenSearch highlight fragment ("... <mark>word</mark> ...", HTML-encoded elsewhere) into HTML that
// contains nothing but text and <mark> elements, whatever the fragment held.
export function highlightToSafeHtml(fragment) {
  const parts = String(fragment || "").split(/(<mark>|<\/mark>)/);
  let html = "";
  let open = false;
  parts.forEach((part) => {
    if (part === "<mark>") {
      if (!open) html += "<mark>";
      open = true;
    } else if (part === "</mark>") {
      if (open) html += "</mark>";
      open = false;
    } else if (part) {
      html += escapeHtml(decodeEntities(part));
    }
  });
  if (open) html += "</mark>";
  return html;
}

export function highlightToText(fragment) {
  return decodeEntities(String(fragment || "").replace(/<\/?mark>/g, ""));
}

export function formatCount(value) {
  const number = Number(value) || 0;
  return number.toLocaleString();
}

// Semantic Search results carry the server's qualification tier (AiSearchQualificationTiers). The grid shows it as
// it is: only a VERIFIED record is called verified; anything without a tier was ranked, not checked field by field.
export const MATCH_TIERS = {
  VERIFIED: "Verified",
  RELATED: "Related",
  POSSIBLE: "Possible",
};

export function matchTierLabel(tier) {
  const value = String(tier || "").toUpperCase();
  if (value === "VERIFIED") return MATCH_TIERS.VERIFIED;
  if (value === "BROADER_RELATED") return MATCH_TIERS.RELATED;
  return MATCH_TIERS.POSSIBLE;
}

export const MATCH_TIER_HINTS = {
  [MATCH_TIERS.VERIFIED]: "Every part of the request was checked on this record.",
  [MATCH_TIERS.RELATED]: "Related to the request, but not every part of it was proven on this record.",
  [MATCH_TIERS.POSSIBLE]: "Ranked by the search as a possible match; not checked part by part.",
};

const plural = (count, one, many) => `${formatCount(count)} ${count === 1 ? one : many}`;

// The line above the Semantic grid, in the server's order and the server's terms: never "close matches" for records
// that were not verified, and "related records" alone when the server found no exact match.
export function semanticResultsSummary(rows, meta) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return "";
  const info = meta || {};
  if (String(info.responseType || "").toUpperCase() === "NO_EXACT_MATCH_WITH_BROADER_RESULTS") {
    return `${plural(list.length, "related record", "related records")} · in ranked order`;
  }
  const counts = { [MATCH_TIERS.VERIFIED]: 0, [MATCH_TIERS.RELATED]: 0, [MATCH_TIERS.POSSIBLE]: 0 };
  list.forEach((row) => {
    counts[row.Match] = (counts[row.Match] || 0) + 1;
  });
  const parts = [];
  if (counts[MATCH_TIERS.VERIFIED]) parts.push(`${formatCount(counts[MATCH_TIERS.VERIFIED])} verified`);
  if (counts[MATCH_TIERS.RELATED]) parts.push(`${formatCount(counts[MATCH_TIERS.RELATED])} related`);
  if (counts[MATCH_TIERS.POSSIBLE]) parts.push(`${formatCount(counts[MATCH_TIERS.POSSIBLE])} possible`);
  return `${plural(list.length, "record", "records")}: ${parts.join(" · ")} · in ranked order`;
}

// "Why it matched" in one short line: about `max` characters of the matched text around its first highlighted word,
// with "…" where it was cut. AI search marks words with <em>, Text Search with <mark>; both come out as <mark>.
// Same safety as highlightToSafeHtml: text and <mark> only.
export function shortHighlightHtml(fragment, max = 120) {
  const segments = [];
  let open = false;
  String(fragment || "")
    .split(/(<mark>|<\/mark>|<em>|<\/em>)/)
    .forEach((part) => {
      if (part === "<mark>" || part === "<em>") open = true;
      else if (part === "</mark>" || part === "</em>") open = false;
      else if (part) segments.push({ text: decodeEntities(part).replace(/\s+/g, " "), marked: open });
    });
  const plain = segments.map((segment) => segment.text).join("");
  if (!plain.trim()) return "";
  let position = 0;
  let firstMark = 0;
  for (let i = 0; i < segments.length; i += 1) {
    if (segments[i].marked) {
      firstMark = position;
      break;
    }
    position += segments[i].text.length;
  }
  const start = Math.max(0, Math.min(firstMark - Math.floor(max / 3), plain.length - max));
  const end = Math.min(plain.length, start + max);
  let html = start > 0 ? "…" : "";
  position = 0;
  segments.forEach((segment) => {
    const from = Math.max(start, position);
    const to = Math.min(end, position + segment.text.length);
    if (to > from) {
      const text = escapeHtml(segment.text.slice(from - position, to - position));
      html += segment.marked ? `<mark>${text}</mark>` : text;
    }
    position += segment.text.length;
  });
  return html.trim() + (end < plain.length ? "…" : "");
}

// "2024-03-14" -> "03/14/2024", the Index Search grid's date form; anything else is shown as it came. 1900-01-01 is
// SQL Server's empty date stored in some index fields, not a real date: shown empty.
export function formatDocumentDate(value) {
  if (String(value || "").slice(0, 10) === "1900-01-01") return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  return match ? `${match[2]}/${match[3]}/${match[1]}` : value || "";
}

// "Interpreted as": what the server's search plan says it looked for, each value exactly as the plan holds it.
export function interpretedAs(plan, templates = []) {
  const value = plan && typeof plan === "object" ? plan : {};
  const list = (items) => (Array.isArray(items) ? items : []).map((item) => String(item || "").trim()).filter(Boolean);
  const chips = [];
  const words = list(value.mustTerms);
  if (words.length) chips.push({ label: "Words", value: words.join(", ") });
  const phrases = list(value.phrases);
  if (phrases.length) chips.push({ label: "Phrase", value: phrases.map((phrase) => `“${phrase}”`).join(", ") });
  const people = list(value.peopleTerms);
  if (people.length) chips.push({ label: "Names", value: people.join(", ") });
  const from = String(value.dateFrom || "").slice(0, 10);
  const to = String(value.dateTo || "").slice(0, 10);
  if (from || to) {
    chips.push({
      label: "Date",
      value: from && to ? `${formatDocumentDate(from)} to ${formatDocumentDate(to)}` : from ? `from ${formatDocumentDate(from)}` : `up to ${formatDocumentDate(to)}`,
    });
  }
  const hinted = list(value.templateHints).map((name) => templateDisplayName(name, templates));
  if (hinted.length) chips.push({ label: "Templates", value: [...new Set(hinted)].join(", ") });
  return chips;
}

// Notes shown under the Semantic summary, each only when the server's response calls for it.
export function semanticResultsNotes(meta) {
  const info = meta || {};
  const notes = [];
  if (info.isLaterPage) {
    notes.push(
      'These are the results of "Check more results" only: follow-up questions, summaries and reports cover this page\'s records, not earlier pages.',
    );
  }
  if (String(info.rangeCoverage || "").toUpperCase() === "RETRIEVED_CANDIDATES_ONLY") {
    notes.push(
      "The date range was checked only among the records this search retrieved, so this is not a list of every record in that range.",
    );
  }
  return notes;
}

// The message shown when Block & Lot Search fails: the server's own validation text for the Block, Lot or repository
// field when it sent one, else a general one.
export function blockLotSearchError(error) {
  const response = error && error.response;
  const status = response && response.status;
  if (status === 401 || status === 403) {
    return "You are not authorized to search the selected repository.";
  }
  if (status === 400 && response.data && typeof response.data === "object") {
    const validationErrors = response.data.errors || response.data.Errors || response.data;
    const keys = Object.keys(validationErrors);
    for (let i = 0; i < keys.length; i += 1) {
      const normalizedKey = keys[i].toLowerCase();
      if (
        normalizedKey.indexOf("block") < 0 &&
        normalizedKey.indexOf("lot") < 0 &&
        normalizedKey.indexOf("repositoryid") < 0
      ) {
        continue;
      }
      const value = validationErrors[keys[i]];
      const message = Array.isArray(value) ? value[0] : value;
      if (typeof message === "string" && message.trim() && message.length <= 250) {
        return message;
      }
    }
  }
  return "Block & Lot Search failed. Please verify the values and try again.";
}
